import CloseIcon from "@mui/icons-material/CloseRounded";
import { IconButton } from "@mui/material";
import { useState } from "react";
import { logError } from "@entrilia/shared/logging";
import { parseUrlFromMarkdown } from "@entrilia/shared/utilities/urlHelper";
import { validResult } from "@entrilia/shared/utilities/validators";
import EditUrlDialog from "../editors/EditUrlDialog";
import type { EntityFieldComponentProps, EntityFieldComponentPropsBase } from "../entityFieldTypes";
import { getInvalidFieldValueError } from "../helpers";
import UrlFieldLink from "../UrlFieldLink";
import EntityFieldTypeWrapper from "./EntityFieldTypeWrapper";
import EntityTextFieldComponent from "./EntityTextFieldComponent";

const EntityUrlField = ({
  value,
  fieldId,
  fieldConfiguration,
  fieldAttributes,
  onChange,
  fieldInputId,
}: EntityFieldComponentPropsBase) => {
  if (typeof value !== "string" && value !== undefined && value !== null) {
    logError(getInvalidFieldValueError(fieldId, fieldConfiguration?.$type, value), "[EntityUrlField]");
    return null;
  }

  return (
    <EntityFieldTypeWrapper
      value={value}
      fieldId={fieldId}
      fieldConfiguration={fieldConfiguration}
      fieldAttributes={fieldAttributes}
      onChange={onChange}
      fieldInputId={fieldInputId}
      ComponentToRender={EntityUrlFieldComponent}
    />
  );
};

export default EntityUrlField;

const EntityUrlFieldComponent = ({
  value,
  onChange,
  fieldId,
  fieldConfiguration,
  fieldAttributes,
  fieldInputId,
}: EntityFieldComponentProps<string>) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const { title, url } = parseUrlFromMarkdown(value ?? "");

  const handleSave = (title: string, url: string) => {
    const markdownUrl = `[${title}](${url})`;
    onChange(markdownUrl, fieldId, validResult());
  };

  const handleClear = () => {
    onChange("", fieldId, validResult());
  };

  const isRequiredField = fieldAttributes.includes("Required");
  const isReadOnlyField = fieldAttributes.includes("ReadOnly");

  return (
    <>
      <EntityTextFieldComponent
        value={value ?? ""}
        fieldId={fieldId}
        fieldConfiguration={fieldConfiguration}
        fieldAttributes={fieldAttributes}
        onChange={onChange}
        fieldInputId={fieldInputId}
        editViaDialog
        onEditDialogOpen={() => setEditDialogOpen(true)}
        displayValueComponent={url ? <UrlFieldLink title={title} url={url} /> : null}
        displayHoverComponent={
          isRequiredField || isReadOnlyField ? null : (
            <IconButton onClick={handleClear}>
              <CloseIcon />
            </IconButton>
          )
        }
      />

      {editDialogOpen && (
        <EditUrlDialog
          onClose={() => setEditDialogOpen(false)}
          title={title}
          url={url?.href ?? ""}
          onSave={handleSave}
        />
      )}
    </>
  );
};
